'use client'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { CardAction } from './cards/card-action'
import type { LessonModuleModalData } from './lesson-module-body'
import { LoveLessonModuleButton } from '../lib/love-lesson-module-button'
import { LessonModuleBody } from './lesson-module-body'

export type { LessonModuleModalData } from './lesson-module-body'

interface LessonModuleModalProps {
  data?: LessonModuleModalData
  isLoading?: boolean
  isError?: boolean
  open: boolean
  onClose: () => void
  renderActivityAction?: CardAction
  showLoveButton?: boolean
  onTagClick?: (type: 'grade' | 'curriculum' | 'contributor', id: string) => void
}

export function LessonModuleModal({
  data,
  isLoading,
  isError,
  open,
  onClose,
  renderActivityAction,
  showLoveButton,
  onTagClick,
}: LessonModuleModalProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <DialogContent className='srd:w-full srd:max-w-170 srd:p-0 srd:overflow-hidden srd:rounded-2xl srd:gap-0 srd:max-h-[90dvh] srd:overflow-y-auto'>
        <DialogHeader className='srd:sr-only'>
          <DialogTitle>{data?.title ?? 'Detail Modul'}</DialogTitle>
          <DialogDescription>Detail modul pelajaran</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className='srd:flex srd:flex-col srd:md:flex-row'>
            <div className='srd:flex srd:flex-col srd:gap-y-4 srd:p-6 srd:md:shrink-0 srd:md:max-w-62 srd:max-w-75 srd:mx-auto'>
              <Skeleton className='srd:w-full srd:aspect-4/3 srd:rounded-xl' />
              <div className='srd:flex srd:flex-col srd:gap-y-2'>
                <Skeleton className='srd:h-4 srd:w-full' />
                <Skeleton className='srd:h-4 srd:w-3/4' />
                <Skeleton className='srd:h-4 srd:w-2/3' />
              </div>
            </div>
            <div className='srd:flex srd:flex-col srd:gap-y-3 srd:p-6 srd:flex-1'>
              <Skeleton className='srd:h-6 srd:w-3/4' />
              <Skeleton className='srd:h-4 srd:w-1/3' />
              <Skeleton className='srd:h-12 srd:w-full' />
              <div className='srd:flex srd:gap-x-2 srd:flex-wrap srd:gap-y-1.5'>
                <Skeleton className='srd:h-6 srd:w-16 srd:rounded-full' />
                <Skeleton className='srd:h-6 srd:w-24 srd:rounded-full' />
              </div>
            </div>
          </div>
        ) : isError ? (
          <div className='srd:flex srd:items-center srd:justify-center srd:p-10'>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-error srd:text-center')}>
              Gagal memuat detail modul. Coba lagi nanti.
            </p>
          </div>
        ) : data ? (
          <LessonModuleBody data={data} onTagClick={onTagClick} />
        ) : null}

        <DialogFooter className='srd:mx-0 srd:mb-0 srd:flex-row srd:items-center srd:justify-between srd:py-3 srd:px-6 srd:gap-x-3'>
          <div className='srd:flex srd:items-center srd:gap-x-1'>
            {data && showLoveButton && <LoveLessonModuleButton lessonModuleId={data.id} />}
          </div>

          <div className='srd:flex srd:items-center srd:gap-x-2'>
            <Button variant='outline' onClick={onClose} className='srd:rounded-full'>
              Tutup
            </Button>

            {data &&
              renderActivityAction &&
              (renderActivityAction.type === 'link' ? (
                <Button className='srd:rounded-full' asChild>
                  {renderActivityAction.renderWrapper(<>Lihat aktivitas</>)}
                </Button>
              ) : (
                <Button className='srd:rounded-full' onClick={renderActivityAction.onClick}>
                  Lihat aktivitas
                </Button>
              ))}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
